<template>
  <div class="dashboard-container p-4">
    <div class="bg-white p-5 rounded-lg shadow mb-4">
      <h1 class="text-2xl font-bold text-gray-800">
        Logistics Dashboard
      </h1>
      <p class="text-gray-600">
        Manage vehicle fleets, transportation, and deliveries.
      </p>
    </div>
        
    <!-- Status Overview Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-blue-600 mb-2">
              8
            </div>
            <div class="text-sm text-gray-600">
              Active Vehicles
            </div>
            <div class="mt-2 text-blue-500 text-sm font-medium">
              On Route
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-green-600 mb-2">
              12
            </div>
            <div class="text-sm text-gray-600">
              Deliveries
            </div>
            <div class="mt-2 text-green-500 text-sm font-medium">
              Completed Today
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-orange-600 mb-2">
              5
            </div>
            <div class="text-sm text-gray-600">
              Pending Pickups
            </div>
            <div class="mt-2 text-orange-500 text-sm font-medium">
              Scheduled Today
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-purple-600 mb-2">
              3
            </div>
            <div class="text-sm text-gray-600">
              Maintenance
            </div>
            <div class="mt-2 text-purple-500 text-sm font-medium">
              Due This Week
            </div>
          </div>
        </template>
      </Card>
    </div>
        
    <!-- Quick Actions Buttons -->
    <div class="flex flex-wrap gap-2 mb-4">
      <Button
        label="Schedule Transport"
        icon="pi pi-calendar-plus"
        severity="secondary"
      />
      <Button
        label="Vehicle Status"
        icon="pi pi-car"
        severity="secondary"
      />
      <Button
        label="Track Shipment"
        icon="pi pi-map-marker"
        severity="secondary"
      />
      <Button
        label="Maintenance Log"
        icon="pi pi-wrench"
        severity="secondary"
      />
    </div>
        
    <!-- Today's Schedule -->
    <div class="bg-white p-5 rounded-lg shadow mb-4">
      <h2 class="text-xl font-semibold mb-4">
        Today's Transport Schedule
      </h2>
      <DataTable
        :value="transportSchedule"
        :rows="5"
        striped-rows
        responsive-layout="scroll"
      >
        <Column
          field="time"
          header="Time"
          style="width: 10%"
        />
        <Column
          field="driver"
          header="Driver"
          style="width: 15%"
        />
        <Column
          field="vehicle"
          header="Vehicle"
          style="width: 15%"
        />
        <Column
          field="project"
          header="Project"
          style="width: 20%"
        />
        <Column
          field="location"
          header="Location"
          style="width: 20%"
        />
        <Column
          field="status"
          header="Status"
          style="width: 10%"
        >
          <template #body="slotProps">
            <Tag
              :value="slotProps.data.status"
              :severity="getStatusSeverity(slotProps.data.status)"
            />
          </template>
        </Column>
        <Column
          field="actions"
          header="Actions"
          style="width: 10%"
        >
          <template #body>
            <Button
              icon="pi pi-ellipsis-h"
              text
              rounded
              aria-label="More Options"
            />
          </template>
        </Column>
      </DataTable>
    </div>
        
    <!-- Vehicle Fleet Status -->
    <div class="bg-white p-5 rounded-lg shadow">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">
          Vehicle Fleet Status
        </h2>
        <div>
          <Button
            label="Fleet Overview"
            icon="pi pi-list"
            text
            class="mr-2"
          />
          <Button
            label="Map View"
            icon="pi pi-map"
            text
          />
        </div>
      </div>
            
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- Vehicle Status Cards -->
        <Card
          v-for="(vehicle, index) in vehicleFleet"
          :key="index"
          class="shadow-sm"
        >
          <template #header>
            <div class="flex justify-between items-center bg-gray-50 px-4 py-2">
              <div>
                <h3 class="text-lg font-semibold">
                  {{ vehicle.name }}
                </h3>
                <span class="text-sm text-gray-600">{{ vehicle.registration }}</span>
              </div>
              <Tag
                :value="vehicle.status"
                :severity="getVehicleSeverity(vehicle.status)"
              />
            </div>
          </template>
          <template #content>
            <div class="p-3">
              <div class="flex justify-between items-center mb-2">
                <span class="text-gray-700">Driver:</span>
                <span class="font-medium">{{ vehicle.driver }}</span>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-gray-700">Current Location:</span>
                <span class="font-medium">{{ vehicle.location }}</span>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-gray-700">Fuel Level:</span>
                <div class="w-1/2">
                  <ProgressBar
                    :value="vehicle.fuel"
                    :class="getFuelClass(vehicle.fuel)"
                  />
                  <span class="text-xs text-right block mt-1">{{ vehicle.fuel }}%</span>
                </div>
              </div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-gray-700">Next Service:</span>
                <span class="font-medium">{{ vehicle.nextService }}</span>
              </div>
                            
              <div class="flex gap-2 justify-end mt-3">
                <Button
                  icon="pi pi-phone"
                  text
                  rounded
                  aria-label="Call Driver"
                />
                <Button
                  icon="pi pi-map-marker"
                  text
                  rounded
                  aria-label="Track Vehicle"
                />
                <Button
                  icon="pi pi-calendar"
                  text
                  rounded
                  aria-label="Schedule"
                />
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

// Sample transport schedule
const transportSchedule = ref([
    { 
        time: '08:00 AM', 
        driver: 'John Smith',
        vehicle: 'Mercedes Sprinter',
        project: 'Corporate Event',
        location: 'Convention Center',
        status: 'Completed'
    },
    { 
        time: '09:30 AM', 
        driver: 'Emma Davis',
        vehicle: 'Ford Transit',
        project: 'Wedding Shoot',
        location: 'Grand Hotel',
        status: 'In Progress'
    },
    { 
        time: '11:00 AM', 
        driver: 'Michael Brown',
        vehicle: 'Iveco Daily',
        project: 'Music Festival',
        location: 'City Park',
        status: 'In Progress'
    },
    { 
        time: '01:30 PM', 
        driver: 'Sarah Wilson',
        vehicle: 'Mercedes Sprinter',
        project: 'TV Commercial',
        location: 'Downtown Studio',
        status: 'Pending'
    },
    { 
        time: '03:00 PM', 
        driver: 'Robert Johnson',
        vehicle: 'Ford Transit',
        project: 'Product Launch',
        location: 'Conference Hall',
        status: 'Pending'
    }
]);

// Sample vehicle fleet
const vehicleFleet = ref([
    {
        name: 'Mercedes Sprinter',
        registration: 'BT21 XYZ',
        driver: 'John Smith',
        location: 'Convention Center',
        fuel: 75,
        status: 'Active',
        nextService: '500 miles'
    },
    {
        name: 'Ford Transit',
        registration: 'KL70 ABC',
        driver: 'Emma Davis',
        location: 'Grand Hotel',
        fuel: 45,
        status: 'Active',
        nextService: '250 miles'
    },
    {
        name: 'Iveco Daily',
        registration: 'MN19 DEF',
        driver: 'Michael Brown',
        location: 'City Park',
        fuel: 60,
        status: 'Active',
        nextService: '1000 miles'
    },
    {
        name: '7.5T Truck',
        registration: 'PQ68 GHI',
        driver: 'Unassigned',
        location: 'Depot',
        fuel: 90,
        status: 'Available',
        nextService: '750 miles'
    }
]);

// Function to determine status severity for tags
const getStatusSeverity = (status) => {
    switch (status.toLowerCase()) {
        case 'completed':
            return 'success';
        case 'in progress':
            return 'info';
        case 'pending':
            return 'warning';
        case 'cancelled':
            return 'danger';
        default:
            return 'secondary';
    }
};

// Function to determine vehicle status severity
const getVehicleSeverity = (status) => {
    switch (status.toLowerCase()) {
        case 'active':
            return 'success';
        case 'available':
            return 'info';
        case 'maintenance':
            return 'warning';
        case 'out of service':
            return 'danger';
        default:
            return 'secondary';
    }
};

// Function to determine fuel level class
const getFuelClass = (level) => {
    if (level < 25) return 'fuel-low';
    if (level < 50) return 'fuel-medium';
    return 'fuel-high';
};
</script>

<style scoped>
.dashboard-container {
    background-color: #f5f7fa;
    min-height: 100vh;
}

.fuel-low .p-progressbar-value {
    background-color: #ef4444 !important;
}

.fuel-medium .p-progressbar-value {
    background-color: #f59e0b !important;
}

.fuel-high .p-progressbar-value {
    background-color: #10b981 !important;
}
</style> 